import { getSupabaseClient } from "@/services/supabaseClient"
import Constants from "expo-constants"
import { Platform } from "react-native"

type OneSignalModule = typeof import("react-native-onesignal")
type OneSignalApi = OneSignalModule["OneSignal"]
type PermissionStatus = "authorized" | "denied" | "unknown"

type PushSubscriptionRef = {
  addEventListener?: (type: string, listener: (...args: unknown[]) => void) => void
  getIdAsync?: () => Promise<string | null>
  getTokenAsync?: () => Promise<string | null>
  id?: string | null
  token?: string | null
  optIn?: () => Promise<void> | void
}

export type OneSignalRegistrationState = {
  appIdConfigured: boolean
  initialized: boolean
  platform: string
  activeUserId: string | null
  permissionStatus: PermissionStatus
  playerId: string | null
  subscriptionId: string | null
  lastSyncSkippedReason: string | null
  lastError: string | null
}

let oneSignalClient: OneSignalApi | null = null
let isInitialized = false
let activeUserId: string | null = null
let sessionSyncQueue: Promise<void> = Promise.resolve()
let subscriptionListenerRegistered = false

let registrationState: OneSignalRegistrationState = {
  appIdConfigured: false,
  initialized: false,
  platform: Platform.OS,
  activeUserId: null,
  permissionStatus: "unknown",
  playerId: null,
  subscriptionId: null,
  lastSyncSkippedReason: null,
  lastError: null,
}

function getOneSignalAppId(): string | null {
  const extra = Constants.expoConfig?.extra as { oneSignalAppId?: string } | undefined
  const candidates = [process.env.EXPO_PUBLIC_ONESIGNAL_APP_ID, extra?.oneSignalAppId]

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }

  return null
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  const normalized = String(error ?? "").trim()
  return normalized.length > 0 ? normalized : "Unknown error"
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null
}

function updateRegistrationState(
  patch: Partial<OneSignalRegistrationState>,
): OneSignalRegistrationState {
  registrationState = {
    ...registrationState,
    ...patch,
    appIdConfigured: Boolean(getOneSignalAppId()),
    platform: Platform.OS,
  }

  return registrationState
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

async function withTimeout<T>(
  operation: PromiseLike<T> | T,
  timeoutMs: number,
  label: string,
): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  try {
    return await Promise.race([Promise.resolve(operation), timeoutPromise])
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }
  }
}

async function getOneSignalClient(): Promise<OneSignalApi | null> {
  if (Platform.OS === "web") {
    return null
  }

  if (oneSignalClient) {
    return oneSignalClient
  }

  try {
    const module = require("react-native-onesignal") as OneSignalModule
    oneSignalClient = module.OneSignal
    return oneSignalClient
  } catch (error) {
    updateRegistrationState({
      initialized: false,
      lastSyncSkippedReason: "client_unavailable",
      lastError: getErrorMessage(error),
    })
    return null
  }
}

async function ensureInitialized(): Promise<OneSignalApi | null> {
  if (Platform.OS === "web") {
    updateRegistrationState({
      initialized: false,
      lastSyncSkippedReason: "web_not_enabled",
    })
    return null
  }

  const appId = getOneSignalAppId()
  if (!appId) {
    updateRegistrationState({
      initialized: false,
      lastSyncSkippedReason: "missing_app_id",
    })
    return null
  }

  const oneSignal = await getOneSignalClient()
  if (!oneSignal) {
    return null
  }

  if (!isInitialized) {
    try {
      oneSignal.initialize(appId)
      isInitialized = true
    } catch (error) {
      const message = getErrorMessage(error)
      if (!message.toLowerCase().includes("already initialized")) {
        updateRegistrationState({
          initialized: false,
          lastSyncSkippedReason: "initialize_failed",
          lastError: message,
        })
        return null
      }
      isInitialized = true
    }
  }

  updateRegistrationState({
    initialized: true,
    lastSyncSkippedReason: null,
    lastError: null,
  })
  return oneSignal
}

function getPushSubscription(oneSignal: OneSignalApi): PushSubscriptionRef | null {
  const user = oneSignal.User as unknown as {
    pushSubscription?: PushSubscriptionRef
    PushSubscription?: PushSubscriptionRef
  }

  return user.pushSubscription ?? user.PushSubscription ?? null
}

async function getPermissionStatus(oneSignal: OneSignalApi): Promise<PermissionStatus> {
  try {
    const notifications = oneSignal.Notifications as unknown as {
      getPermissionAsync?: () => Promise<boolean>
      permission?: boolean | string
    }

    if (typeof notifications.getPermissionAsync === "function") {
      return (await notifications.getPermissionAsync()) ? "authorized" : "denied"
    }

    if (typeof notifications.permission === "boolean") {
      return notifications.permission ? "authorized" : "denied"
    }

    if (typeof notifications.permission === "string") {
      const normalized = notifications.permission.trim().toLowerCase()
      if (["authorized", "granted", "true"].includes(normalized)) return "authorized"
      if (["denied", "false"].includes(normalized)) return "denied"
    }
  } catch {
    return "unknown"
  }

  return "unknown"
}

async function getSubscriptionIdentifier(subscription: PushSubscriptionRef | null): Promise<{
  playerId: string | null
  subscriptionId: string | null
}> {
  if (!subscription) {
    return { playerId: null, subscriptionId: null }
  }

  let subscriptionId: string | null = null
  let token: string | null = null

  try {
    if (typeof subscription.getIdAsync === "function") {
      subscriptionId = asString(
        await withTimeout(subscription.getIdAsync(), 2500, "onesignal.getIdAsync"),
      )
    }
  } catch {
    subscriptionId = null
  }

  try {
    if (typeof subscription.getTokenAsync === "function") {
      token = asString(
        await withTimeout(subscription.getTokenAsync(), 2500, "onesignal.getTokenAsync"),
      )
    }
  } catch {
    token = null
  }

  return {
    playerId: subscriptionId ?? token ?? asString(subscription.id) ?? asString(subscription.token),
    subscriptionId: subscriptionId ?? asString(subscription.id),
  }
}

async function waitForSubscriptionIdentifier(oneSignal: OneSignalApi): Promise<{
  playerId: string | null
  subscriptionId: string | null
}> {
  const deadline = Date.now() + 3000
  let identifiers = await getSubscriptionIdentifier(getPushSubscription(oneSignal))

  while (!identifiers.playerId && Date.now() < deadline) {
    await wait(300)
    identifiers = await getSubscriptionIdentifier(getPushSubscription(oneSignal))
  }

  return identifiers
}

async function registerDevice(oneSignal: OneSignalApi): Promise<boolean> {
  const client = getSupabaseClient()
  const userId = activeUserId

  if (!client || !userId) {
    updateRegistrationState({ lastSyncSkippedReason: "missing_supabase_session" })
    return false
  }

  const permissionStatus = await getPermissionStatus(oneSignal)
  const subscription = getPushSubscription(oneSignal)

  try {
    if (permissionStatus === "authorized" && typeof subscription?.optIn === "function") {
      await withTimeout(Promise.resolve(subscription.optIn()), 3000, "onesignal.optIn")
    }
  } catch {
    // OneSignal can still expose subscription ids when explicit opt-in fails.
  }

  const { playerId, subscriptionId } = await waitForSubscriptionIdentifier(oneSignal)
  updateRegistrationState({
    activeUserId: userId,
    permissionStatus,
    playerId,
    subscriptionId,
  })

  if (!playerId && !subscriptionId) {
    updateRegistrationState({
      lastSyncSkippedReason:
        permissionStatus === "denied" ? "permission_denied" : "missing_subscription_identifier",
    })
    return false
  }

  const { error } = await client.from("push_notification_devices").upsert(
    {
      user_id: userId,
      provider: "onesignal",
      platform: Platform.OS,
      permission_status: permissionStatus,
      player_id: playerId,
      subscription_id: subscriptionId,
      is_active: true,
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: "user_id,platform,provider" },
  )

  if (error) {
    updateRegistrationState({
      lastSyncSkippedReason: "register_device_failed",
      lastError: error.message,
    })
    return false
  }

  updateRegistrationState({
    lastSyncSkippedReason: null,
    lastError: null,
  })
  return true
}

async function registerSubscriptionListener(oneSignal: OneSignalApi): Promise<void> {
  if (subscriptionListenerRegistered) {
    return
  }

  const subscription = getPushSubscription(oneSignal)
  if (!subscription?.addEventListener) {
    return
  }

  try {
    subscription.addEventListener("change", () => {
      if (activeUserId) {
        void registerDevice(oneSignal)
      }
    })
    subscriptionListenerRegistered = true
  } catch {
    subscriptionListenerRegistered = false
  }
}

async function loginOneSignal(oneSignal: OneSignalApi, userId: string): Promise<boolean> {
  try {
    await withTimeout(Promise.resolve(oneSignal.login(userId)), 4000, "onesignal.login")
    return true
  } catch (error) {
    updateRegistrationState({
      lastSyncSkippedReason: "login_failed",
      lastError: getErrorMessage(error),
    })
    return false
  }
}

async function logoutOneSignal(oneSignal: OneSignalApi): Promise<void> {
  try {
    await withTimeout(Promise.resolve(oneSignal.logout()), 3000, "onesignal.logout")
  } catch {
    // Best-effort logout.
  }
}

async function syncOneSignalSessionInternal(user: { id: string } | null): Promise<void> {
  const oneSignal = await ensureInitialized()
  if (!oneSignal) {
    return
  }

  await registerSubscriptionListener(oneSignal)

  if (!user?.id) {
    if (activeUserId) {
      await logoutOneSignal(oneSignal)
      activeUserId = null
      updateRegistrationState({
        activeUserId: null,
        lastSyncSkippedReason: null,
      })
    }
    return
  }

  if (activeUserId !== user.id) {
    const loggedIn = await loginOneSignal(oneSignal, user.id)
    if (!loggedIn) return
    activeUserId = user.id
  }

  const registered = await registerDevice(oneSignal)
  if (!registered) {
    await wait(900)
    await registerDevice(oneSignal)
  }
}

export async function syncOneSignalSession(user: { id: string } | null): Promise<void> {
  const task = sessionSyncQueue
    .catch(() => undefined)
    .then(async () => {
      await syncOneSignalSessionInternal(user)
    })

  sessionSyncQueue = task
  await task
}

export function getOneSignalRegistrationState(): OneSignalRegistrationState {
  return registrationState
}

export function isOneSignalConfigured() {
  return Boolean(getOneSignalAppId())
}
